import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import DocsHeader from '@/components/docs/DocsHeader';
import DocsFooter from '@/components/docs/DocsFooter';
import AppIcon from '@/components/docs/AppIcon';
import { DocsSearchField } from '@/components/docs/DocsSearch';
import { DOCS_GROUPS, docPath, docsInGroup } from '@/data/docs/meta';
import { CONTACTS } from '@/seo/site';

const EXAMPLES = ['подключить кассу', 'закрыть смену', 'кухонный принтер', 'онлайн-оплата', 'импорт меню'];

export default function Docs() {
  const { hash, key } = useLocation();

  // Хлебные крошки статей ведут на группу: /docs#hall.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: 'start' });
  }, [hash, key]);

  return (
    <div className="min-h-screen bg-white">
      <DocsHeader search={false} />

      <main>
        <section className="border-b border-ink/[0.07] bg-ink/[0.022]">
          <div className="mx-auto max-w-[72rem] px-5 pb-12 pt-14 sm:px-8 sm:pb-16 sm:pt-20">
            <h1 className="text-balance text-[clamp(2.25rem,5vw,3.5rem)] font-medium leading-[1.04] tracking-[-0.03em] text-ink">
              Документация Resstro
            </h1>
            <p className="mt-4 max-w-[40rem] text-[17px] leading-relaxed text-ink/70">
              Инструкции по кассе, админ-панели, электронному меню и кабинетам партнёров. Названия
              кнопок в статьях совпадают с тем, что вы видите на экране.
            </p>
            <div className="mt-8 max-w-[46rem]">
              <DocsSearchField variant="hero" examples={EXAMPLES} />
            </div>
            <Link
              to={docPath('start')}
              className="group mt-7 inline-flex items-center gap-2 text-[15px] font-medium text-ink transition-colors hover:text-orange-700"
            >
              Впервые в Resstro? Быстрый старт
              <ArrowRight
                size={16}
                strokeWidth={2}
                className="transition-transform duration-200 ease-out group-hover:translate-x-0.5"
              />
            </Link>
          </div>
        </section>

        <div className="mx-auto max-w-[72rem] px-5 py-8 sm:px-8 sm:py-12">
          {DOCS_GROUPS.map((group) => (
            <section
              key={group.id}
              id={group.id}
              aria-labelledby={`group-${group.id}`}
              className="grid scroll-mt-20 gap-3 border-t border-ink/[0.08] py-8 first:border-t-0 md:grid-cols-[13rem_1fr] md:gap-10"
            >
              <h2 id={`group-${group.id}`} className="text-[15px] font-medium text-ink md:pt-3">
                {group.title}
              </h2>
              <ul className="grid gap-1 sm:grid-cols-2 xl:grid-cols-3">
                {docsInGroup(group.id).map((doc) => (
                  <li key={doc.slug}>
                    <Link
                      to={docPath(doc.slug)}
                      className="flex h-full items-start gap-3.5 rounded-xl p-3 transition-colors duration-200 hover:bg-ink/[0.035]"
                    >
                      <AppIcon slug={doc.slug} size={40} />
                      <span className="min-w-0">
                        <span className="block text-[15.5px] font-medium leading-snug text-ink">{doc.title}</span>
                        <span className="mt-1 block text-[14px] leading-snug text-ink/65">{doc.summary}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section className="mx-auto max-w-[72rem] px-5 pb-20 sm:px-8">
          <div className="flex flex-col gap-5 rounded-2xl bg-ink/[0.03] px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <div>
              <h2 className="text-[17px] font-medium text-ink">Не нашли ответ?</h2>
              <p className="mt-1 text-[14.5px] leading-relaxed text-ink/65">
                Поддержка отвечает в WhatsApp и по почте, поможем с настройкой.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={CONTACTS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-dark h-11 px-5 py-0 text-[14px]"
              >
                Написать в WhatsApp
              </a>
              <a href={`mailto:${CONTACTS.supportEmail}`} className="btn-outline h-11 px-5 py-0 text-[14px]">
                {CONTACTS.supportEmail}
              </a>
            </div>
          </div>
        </section>
      </main>

      <DocsFooter />
    </div>
  );
}
